import { Formik } from 'formik';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link } from 'react-router-dom';

import AuthForm from './AuthForm';
import AuthInput from './AuthInput';
import AuthSub from './AuthSub';
import AuthSubmit from './AuthSubmit';
import { signUpSchema } from './schemas/sign-up.schema';

import { AuthRoutes } from '@/interfaces/auth/AuthRoutes.enum';

type PropTypes = {
	handleSubmit: (username: string, password: string) => Promise<void>;
	loading: boolean;
};

export default function SignUpForm({ handleSubmit, loading }: PropTypes) {
	const [step, setStep] = useState<1 | 2>(1);
	const recaptchaRef = useRef<ReCAPTCHA>(null);

	const initialValues = {
		username: '',
		password: '',
		recaptcha: '',
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={signUpSchema}
			validateOnBlur={false}
			validateOnChange={false}
			onSubmit={async ({ username, password }) => {
				if (step === 2) {
					await handleSubmit(username, password);
				}
			}}
		>
			{({ errors, touched, validateField, values, setFieldValue }) => (
				<AuthForm>
					<AuthInput
						name="username"
						label="Username"
						type="email"
						placeholder="Email"
						error={!!errors.username}
						touched={touched.username}
						data-test="sign-up-username"
						disabled={step === 2}
					/>

					{step === 2 && (
						<>
							<AuthInput
								name="password"
								label="Password"
								type="password"
								enableCustomValidations={true}
								placeholder="Password"
								error={!!errors.password}
								touched={touched.password}
								data-test="sign-up-password"
							/>
							<AuthInput
								name="recaptcha"
								label="Please verify you are human"
								type="recaptcha"
								error={!!errors.recaptcha}
								touched={touched.recaptcha}
								ref={recaptchaRef}
							/>
						</>
					)}

					<div className="flex gap-3 mt-1">
						<button
							type="button"
							data-test="sign-up-next"
							className={`px-4 flex-1 py-3 transition-colors rounded-3xl ${
								step === 1
									? 'bg-purpleBrand-500 hover:bg-purpleBrand-600 text-white'
									: 'glassy-shadow-inset-inward'
							}`}
							onClick={async () => {
								if (step === 1) {
									await validateField('username');
									if (!errors.username && values.username) {
										setStep(2);
									}
								} else {
									// Reset captcha when going back
									if (recaptchaRef.current) {
										recaptchaRef.current.reset();
									}
									setFieldValue('recaptcha', '');
									setStep(1);
								}
							}}
						>
							{step === 1 ? 'Next' : 'Back'}
						</button>

						{step === 2 && (
							<AuthSubmit
								label="Sign Up"
								isLoading={loading}
								data-test="sign-up-submit"
								className="flex-1 bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900"
							/>
						)}
					</div>

					<AuthSub>
						<p>
							Already have an account?{' '}
							<Link
								className="font-medium text-purpleBrand-500"
								to={AuthRoutes.SignIn}
								data-test="link-sign-in"
							>
								Sign in
							</Link>
						</p>
					</AuthSub>
				</AuthForm>
			)}
		</Formik>
	);
}
